<!--

	There are desktop view buttons that contains an icon, such as glyphicon-edit, next to it description link,
	such as Edit.
	This component makes the description disappears and the button become rounded for small devices.

-->

<template>
	<a class="mia-ResponsiveButton" :class="getButtonClasses()" @click="$emit('click')">
		<promise-loader :promise="loadingPromise">
			<div v-if="icon" class="mia-responsiveButton-icon">
				<inline-svg class="mia-Icon" :src="icon" />
			</div>

			<div v-else-if="glyphicon" class="mia-responsiveButton-glyphicon">
				<span class="glyphicon" :class="[`glyphicon-${glyphicon}`]" />
			</div>
		</promise-loader>

		<span v-if="$slots.default" class="mia-responsiveButton-description" :class="{ 'hidden-xs': icon || glyphicon }">
			<slot />
		</span>
	</a>
</template>
<script lang="ts">
import { Component, Prop, toNative, Vue } from "vue-facing-decorator";
import PromiseLoader from "@/common/PromiseLoader.vue";
import InlineSvg from "vue-inline-svg";

const BUTTON_OUTLINED_CLASS = 'btn-outline';
const BUTTON_LINK_CLASS = 'btn-link';
const BUTTON_PRIMARY_CLASS = 'mia-btn-primary';
const BUTTON_ENCOURAGE_CLASS = 'mia-btn-encourage';
const BUTTON_WARNING_CLASS = 'mia-btn-warning';
const BUTTON_REVERSE_CLASS = 'mia-ResponsiveButton--reverse';
const BUTTON_HIDE_ICON_NEAR_CONTENT_CLASS = 'mia-ResponsiveButton--hide-icon-on-sm';

@Component({
	emits: ['click'],
	components: {
		PromiseLoader,
		InlineSvg,
	}
})
export class ResponsiveButton extends Vue {
	@Prop({ required: false, type: Promise, default: null }) loadingPromise!: Promise<any>;
	@Prop({ required: false, type: String }) icon!: string;
	@Prop({ required: false, type: String }) glyphicon!: string;
	@Prop({ required: false, type: String }) private color!: string;
	@Prop({ required: false, type: Boolean, default: false }) private hideIconNearContent!: boolean;
	@Prop({ required: false, type: Boolean, default: false }) private reverse!: boolean;
	@Prop({ required: false, type: Boolean, default: false }) private outlined!: boolean;
	@Prop({ required: false, type: Boolean, default: false }) private link!: boolean;

	getButtonClasses(): string[] {
		var classArray = ['btn'];

		switch (this.color) {
			case 'encourage':
				classArray.push(BUTTON_ENCOURAGE_CLASS);
				break;
			case 'warning':
				classArray.push(BUTTON_WARNING_CLASS);
				break;
			case 'primary':
				classArray.push(BUTTON_PRIMARY_CLASS);
				break;
			default:
				if (!this.link) {
					classArray.push(BUTTON_PRIMARY_CLASS);
				}
				break;
		}

		if (this.outlined) {
			classArray.push(BUTTON_OUTLINED_CLASS);
		}

		if (this.reverse) {
			classArray.push(BUTTON_REVERSE_CLASS);
		}

		if (this.link) {
			classArray.push(BUTTON_LINK_CLASS);
		}

		if (this.hideIconNearContent && this.$slots.default) {
			classArray.push(BUTTON_HIDE_ICON_NEAR_CONTENT_CLASS);
		}

		return classArray;
	}
}

export default toNative(ResponsiveButton);
</script>

<style lang="scss">
.mia-ResponsiveButton {
	padding: theme.$mia-padding-panel theme.$mia-padding-panel-md;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	column-gap: theme.$mia-gap-size;
	width: fit-content;
	min-height: theme.$mia-responsive-button-size;
	min-width: theme.$mia-responsive-button-size;

	.mia-responsiveButton-icon {
		height: theme.$mia-button-icon-size;
		width: theme.$mia-button-icon-size;

		> svg {
			width: 100%;
			height: 100%;

			path {
				fill: currentColor;
			}
		}
	}

	.mia-responsiveButton-glyphicon {
		height: theme.$mia-button-icon-size;
		width: theme.$mia-button-icon-size;
		display: flex;
		justify-content: center;
		align-items: center;

		> .glyphicon {
			top: unset;
			width: theme.$font-size-base;
			height: theme.$font-size-base;
		}
	}

	[disabled] {
		.mia-responsiveButton-icon,
		.mia.responsiveButton-glyphicon {
			display: none;
		}
	}

	&.btn-outline,
	&.btn-link {
		> .mia-responsiveButton-icon > svg {
			fill: currentColor;
		}
	}

	@media (max-width: theme.$screen-xs-max) {
		padding: theme.$mia-padding-panel;
	}

	&--reverse {
		flex-direction: row-reverse;
	}

	&--hide-icon-on-sm {
		@media (min-width: theme.$screen-xs-max) {
			.mia-responsiveButton-icon,
			.mia-responsiveButton-glyphicon {
				display: none;
			}
		}
	}
}
</style>
